import React, { useState, useRef, useEffect, useCallback } from "react";
import { Editor } from "@tiptap/core";
import RichTextEditor from "../../../../../../../../../components/RichTextEditor/RichTextEditor";
import RichTextEditorToolbar from "../../../../../../../../../components/RichTextEditor/RichTextEditorToolbar";
import ImageIcon from "../../../../../../../../../assets/svg/v1/image-icon";
import AttachFilesIcon from "../../../../../../../../../assets/svg/v1/attach-files-icon";
import { visibleScrollbar } from "../../../../../../../../../utils/functions/get-visible-scrollbar";
import ChevronDownIcon from "../../../../../../../../../assets/svg/v1/chevron-down";
import ChevronUpIcon from "../../../../../../../../../assets/svg/chevron-up-icon";
import InfoBtnIcon from "../../../../../../../../../assets/svg/info-btn-icon";
import { Tooltip } from "../../../../../../../../../components/Tooltip/Tooltip";
import AttachmentPreviewModal from "../../../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { AttachmentPreviewItem } from "../../../../../../../../Communications/utils/types";
import { ANNOUNCEMENT_BANNER_DISMISSED_KEY } from "../../../../../../../../../utils/constants/announcementBannerInfo";
import { SessionStorageUtil } from "../../../../../../../../../utils/storage/storageUtils";
import TicketDescriptionDisabledToolbar from "./TicketDescriptionDisabledToolbar";
import { useGetTicketCategoriesQuery } from "../../../../../../../../../redux/services/helpdeskApi";
import { getHelpdeskLabels } from "../../../../../../../../../utils/constants/mappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../redux/store";

interface TicketDescriptionBoxProps {
  initialContent: string;
  onSave: (html: string) => void;
  onChange?: (html: string) => void;
  onImageInsert?: (file: File, onUrlReplace: (s3Url: string) => void) => void;
  mentionActiveUsersList?: {
    _id: string;
    name: string;
    email: string;
    profile_picture: string | null;
  }[];
  setTaggedUsers?: (taggedUsers: string[]) => void;
  ticketCategory?: string;
}

interface DescriptionHeadingProps {
  onClick?: (event: React.MouseEvent) => void;
  showGuide?: boolean;
}

const DESCRIPTION_GUIDE_CONTENT = (
  <>
    <p>
      Add ticket description and please be as concise as possible and include
      all relevant information including:
    </p>
    <ul className="mt-2 list-disc pl-5 space-y-1">
      <li>Tenants/guarantors name</li>
      <li>Email address</li>
      <li>Room number</li>
      <li>Contract ID</li>
      <li>Amounts (where applicable)</li>
      <li>Room type or tenancy length</li>
    </ul>
  </>
);

const PLACEHOLDER_NODE = (
  <div className="text-base text-gray-placeholder dark:text-gray-placeholder-dk">
    {DESCRIPTION_GUIDE_CONTENT}
  </div>
);

const DEFAULT_PLACEHOLDER_NODE = (
  <div className="text-base text-gray-placeholder dark:text-gray-placeholder-dk">
    Add ticket description
  </div>
);

const DescriptionHeading: React.FC<DescriptionHeadingProps> = ({ onClick, showGuide }) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      role="presentation"
      onClick={onClick}
      className={`flex items-center gap-1 px-4 pt-3 shrink-0 w-full ${onClick ? "cursor-text" : ""}`}
    >
      <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
        {helpdeskLabels.ticketSingular} Description
      </span>
      {showGuide && (
        <Tooltip
          content={
            <div className="text-md font-normal">{DESCRIPTION_GUIDE_CONTENT}</div>
          }
          side="top"
          maxWidth={320}
        >
          <span
            onClick={stopPropagation}
            className="inline-flex items-center justify-center text-gray-placeholder dark:text-gray-placeholder-dk cursor-pointer"
          >
            <InfoBtnIcon className="w-4 h-4" />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const TOOLBAR_SCHEMA = {
  history: true,
  typography: { fontFamily: true, fontSize: true },
  textFormatting: {
    bold: true,
    italic: true,
    underline: true,
    strikethrough: true,
  },
  textStyling: { textColor: true },
  contentStructure: {
    lists: true,
    alignment: true,
    indentation: true,
    blockquotes: false,
  },
  media: { links: true, images: false, attachments: false },
  interactive: { emojis: false },
  utilities: { clearFormatting: false },
  mentions: { mentions: true },
};

const isHtmlContentEmpty = (html: string | undefined | null): boolean => {
  if (!html) return true;
  if (html === "<p></p>") return true;
  if (html?.includes("<img")) return false;
  const textOnly = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();
  return textOnly === "";
};

// Clicks inside these floating portals must not trigger a click-outside save
const PORTAL_SELECTORS =
  "[data-toolbar-portal], [data-image-toolbar-portal], [data-mention-portal], [data-link-portal], [data-link-bubble], [tippy-root], .tippy-box";

const isInsideAnyPortal = (target: Node): boolean => {
  const portals = document.querySelectorAll(PORTAL_SELECTORS);
  for (const el of portals) {
    if (el.contains(target)) return true;
  }
  return false;
};

const TicketDescriptionBox: React.FC<TicketDescriptionBoxProps> = ({
  initialContent,
  onSave,
  onChange,
  onImageInsert,
  mentionActiveUsersList,
  setTaggedUsers,
  ticketCategory,
}) => {
  const { data: ticketCategories } = useGetTicketCategoriesQuery();
  const isHelpdeskSupport =
    ticketCategories?.find((c) => c.key === ticketCategory)
      ?.display_name === "Helpdesk Support";
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [pendingFirstLineFocus, setPendingFirstLineFocus] = useState(false);

  const [previewOpen, setPreviewOpenRaw] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const prevServerContentRef = useRef<string | null>(null);
  const clickCoordsRef = useRef<{ x: number; y: number } | null>(null);
  const clickedImageSrcRef = useRef<string | null>(null);
  const contentRef = useRef(content);
  contentRef.current = content;
  const previewAttachmentsRef = useRef<AttachmentPreviewItem[]>([]);
  const previewOpenRef = useRef(false);
  const previewClickedSrcRef = useRef<string | null>(null);

  const isBannerVisible =
    SessionStorageUtil.getItem(ANNOUNCEMENT_BANNER_DISMISSED_KEY) !== "true";

  editorRef.current = editor;

  const handlePreviewOpenChange = useCallback((open: boolean) => {
    if (open) {
      previewOpenRef.current = true;
    } else {
      setTimeout(() => {
        previewOpenRef.current = false;
      }, 0);

      const src = previewClickedSrcRef.current;
      setTimeout(() => {
        if (src && editorScrollRef.current) {
          const imgs = editorScrollRef.current.querySelectorAll("img");
          for (const img of imgs) {
            if (img.src === src) {
              img.scrollIntoView({ block: "center", behavior: "instant" });
              break;
            }
          }
        }
      }, 100);
    }
    setPreviewOpenRaw(open);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (previewOpenRef.current) return;
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (isInsideAnyPortal(target)) return;
      // While the link edit popup is open, editing a link is a sub-task of
      // editing the description — never drop out of edit mode. The popup closes
      // itself, after which normal click-outside behavior resumes.
      if (document.querySelector("[data-link-portal]")) {
        return;
      }

      setIsEditing(false);
      const ed = editorRef.current;
      if (ed) {
        const rawHtml = ed.getHTML();
        const normalized = isHtmlContentEmpty(rawHtml) ? "" : rawHtml;
        setContent(normalized);
        onSave(normalized);
      }
    },
    [onSave]
  );

  const handleEditorReady = useCallback((editorInstance: Editor) => {
    setEditor(editorInstance);
  }, []);

  const scrollCursorIntoView = useCallback(() => {
    const ed = editorRef.current;
    const scrollContainer = editorScrollRef.current;
    if (!ed || !scrollContainer) return;

    requestAnimationFrame(() => {
      try {
        const { from } = ed.state.selection;
        const coords = ed.view.coordsAtPos(from);
        const containerRect = scrollContainer.getBoundingClientRect();
        const cursorBottom =
          coords.bottom - containerRect.top + scrollContainer.scrollTop;
        const visibleBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight;

        if (cursorBottom > visibleBottom - 20) {
          scrollContainer.scrollTop =
            cursorBottom - scrollContainer.clientHeight + 40;
        }
      } catch {
        // coordsAtPos may throw before the editor view is fully ready
      }
    });
  }, []);

  const handleMessageChange = useCallback(
    (html: string) => {
      const normalized = isHtmlContentEmpty(html) ? "" : html;
      setContent(normalized);
      onChange?.(normalized);
      scrollCursorIntoView();
    },
    [onChange, scrollCursorIntoView]
  );

  const handleReadModeClick = useCallback((e: React.MouseEvent) => {
    clickCoordsRef.current = { x: e.clientX, y: e.clientY };
    const target = e.target as HTMLElement;
    const imgEl =
      target.tagName === "IMG"
        ? (target as HTMLImageElement)
        : (target
            .closest("[data-node-view-wrapper]")
            ?.querySelector("img") as HTMLImageElement | null);
    clickedImageSrcRef.current = imgEl?.src ?? null;
    setIsEditing(true);
  }, []);

  const handleHeadingClick = useCallback(() => {
    setPendingFirstLineFocus(true);
    setIsEditing(true);
  }, []);

  const handleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
  }, []);

  const handleCollapse = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (editorScrollRef.current) editorScrollRef.current.scrollTop = 0;
    setIsExpanded(false);
  }, []);

  // Swap one image src inside the ProseMirror document in a single atomic transaction
  const replaceImageSrc = useCallback(
    (ed: Editor, oldSrc: string, newSrc: string) => {
      if (ed.isDestroyed) return;
      const { state } = ed;
      const { tr } = state;
      let modified = false;
      state.doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.src === oldSrc) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: newSrc });
          modified = true;
        }
      });
      if (modified) ed.view.dispatch(tr);
    },
    []
  );

  // Insert a base64 preview immediately, then replace it with the S3 URL on upload
  const insertImageFile = useCallback(
    (file: File, editorInstance?: Editor, insertAtEnd?: boolean) => {
      const ed = editorInstance || editorRef.current;
      if (!ed) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target?.result) return;
        const dataUrl = e.target.result as string;
        const img = new window.Image();
        img.src = dataUrl;
        img.onload = () => {
          if (ed.isDestroyed) return;
          const containerWidth = ed.view.dom.clientWidth;
          const width =
            img.naturalWidth > containerWidth
              ? "100%"
              : `${img.naturalWidth}px`;
          ed.chain()
            .focus(insertAtEnd ? "end" : undefined)
            .insertContent({
              type: "paragraph",
              content: [{ type: "image", attrs: { src: dataUrl, width } }],
            })
            .focus()
            .run();
          // Upload after inserting so we know the exact dataUrl to replace
          onImageInsert?.(file, (s3Url: string) => {
            replaceImageSrc(ed, dataUrl, s3Url);
          });
        };
      };
      reader.readAsDataURL(file);
    },
    [onImageInsert, replaceImageSrc]
  );

  const handleImageUploadClick = useCallback((e: React.MouseEvent) => {
    e?.stopPropagation();
    // Mount the editor before the OS dialog opens so it's ready by the time
    // the user picks a file, avoiding the pendingImages queue path.
    setIsEditing(true);
    imageInputRef.current?.click();
  }, []);

  const handleImageFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files?.length === 0) return;
      const images = Array.from(files)?.filter((f) =>
        f.type?.startsWith("image/")
      );
      if (images?.length === 0) return;

      if (!isEditing) {
        // Editor not yet mounted — queue for the pendingImages effect to process
        setIsEditing(true);
        setPendingImages(images);
      } else {
        images?.forEach((file) => insertImageFile(file));
      }
      if (imageInputRef.current) imageInputRef.current.value = "";
    },
    [isEditing, insertImageFile]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      const prosemirrorAlreadyHandled = e.nativeEvent.defaultPrevented;
      e?.preventDefault();
      e?.stopPropagation();
      setIsDragOver(false);

      const files = e?.dataTransfer?.files;
      if (!files || files?.length === 0) return;
      const images = Array.from(files)?.filter((f) =>
        f.type?.startsWith("image/")
      );
      if (images?.length === 0) return;

      if (!isEditing) {
        setIsEditing(true);
        setPendingImages(images);
      } else if (prosemirrorAlreadyHandled) {
        images?.forEach((file) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            if (!ev.target?.result) return;
            const dataUrl = ev.target.result as string;
            const ed = editorRef.current;
            onImageInsert?.(file, (s3Url: string) => {
              if (ed) replaceImageSrc(ed, dataUrl, s3Url);
            });
          };
          reader.readAsDataURL(file);
        });
      } else {
        images?.forEach((file) => insertImageFile(file, undefined, true));
      }
    },
    [isEditing, insertImageFile, onImageInsert, replaceImageSrc]
  );

  const isContentEmpty = isHtmlContentEmpty(content);
  const showEditor = isEditing || !isContentEmpty;

  const innerInsetClass = "px-4 pt-3";
  const innerColumnClass = `flex-1 min-h-0 flex flex-col ${innerInsetClass}`;

  const dragProps = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  // Flush queued images once the editor mounts.
  // setPendingImages([]) lives inside the timer so that if the editor is
  // recreated mid-flight (e.g. Mention extension rebuilds), the cleanup cancels
  // the timer but preserves pendingImages for the next instance to retry.
  useEffect(() => {
    if (!editor || pendingImages?.length === 0) return;
    const files = [...pendingImages];
    const timer = setTimeout(() => {
      const ed = editorRef.current;
      if (ed) {
        setPendingImages([]);
        files?.forEach((f) => insertImageFile(f, ed));
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [editor, pendingImages, insertImageFile]);

  // Apply server updates only when not editing; editing guards unsaved changes
  useEffect(() => {
    if (isEditing) return;
    const serverKey = initialContent ?? "";
    if (prevServerContentRef.current === serverKey) return;
    prevServerContentRef.current = serverKey;
    const normalized = isHtmlContentEmpty(initialContent) ? "" : initialContent;
    setContent(normalized);
  }, [initialContent, isEditing]);

  useEffect(() => {
    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, handleClickOutside]);

  useEffect(() => {
    if (isEditing) setIsExpanded(false);
  }, [isEditing]);

  useEffect(() => {
    const el = editorScrollRef.current;
    if (!el || isEditing || isContentEmpty) {
      setIsOverflowing(false);
      return;
    }
    const check = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
    // isExpanded in deps: collapsing restores max-h, so overflow must be re-evaluated
  }, [isEditing, content, isContentEmpty, isExpanded]);

  useEffect(() => {
    const el = editorScrollRef.current;
    if (!el || !isEditing) return;

    const openImagePreview = (e: Event) => {
      const target = e.target as HTMLElement;
      const imgEl =
        target.tagName === "IMG"
          ? (target as HTMLImageElement)
          : (target
              .closest("[data-node-view-wrapper]")
              ?.querySelector("img") as HTMLImageElement | null);
      if (!imgEl) return;

      e.preventDefault();
      e.stopPropagation();

      const allImgs = Array.from(el.querySelectorAll("img"));
      const seen = new Set<string>();
      const items: AttachmentPreviewItem[] = [];
      let clickedIndex = 0;

      allImgs.forEach((img) => {
        const src = img.src;
        if (!src || seen.has(src)) return;
        seen.add(src);
        if (src === imgEl.src) clickedIndex = items.length;
        const segments = src.split("/");
        const rawName = segments[segments.length - 1]?.split("?")[0] || "image";
        items.push({
          s3_url: src,
          file_name: decodeURIComponent(rawName),
          content_type: "image/png",
          timestamp: new Date().toISOString(),
        });
      });

      if (items.length > 0) {
        previewAttachmentsRef.current = items;
        previewClickedSrcRef.current = imgEl.src;
        setPreviewIndex(clickedIndex);
        handlePreviewOpenChange(true);
      }
    };

    el.addEventListener("dblclick", openImagePreview, true);
    return () => el.removeEventListener("dblclick", openImagePreview, true);
  }, [isEditing, handlePreviewOpenChange]);

  useEffect(() => {
    if (!isEditing || !editor || editor.isDestroyed) return;
    const coords = clickCoordsRef.current;
    const clickedImageSrc = clickedImageSrcRef.current;
    clickCoordsRef.current = null;
    clickedImageSrcRef.current = null;
    const t = window.setTimeout(() => {
      if (editor.isDestroyed) return;
      if (clickedImageSrc) {
        let imagePos: number | null = null;
        editor.state.doc.descendants((node, pos) => {
          if (
            imagePos === null &&
            node.type.name === "image" &&
            node.attrs.src === clickedImageSrc
          ) {
            imagePos = pos;
          }
        });
        if (imagePos !== null) {
          editor.chain().focus().setNodeSelection(imagePos).run();
          return;
        }
      }
      if (coords) {
        try {
          const pos = editor.view.posAtCoords({
            left: coords.x,
            top: coords.y,
          });
          if (pos) {
            editor.chain().focus().setTextSelection(pos.pos).run();
            return;
          }
        } catch {
          // posAtCoords can throw before the view is fully ready; fall through
        }
      }
      if (!isHtmlContentEmpty(contentRef.current)) {
        editor.chain().focus("end").run();
      } else {
        editor.chain().focus("start").run();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [isEditing, editor]);

  useEffect(() => {
    if (!pendingFirstLineFocus) return;
    if (!isEditing) {
      setPendingFirstLineFocus(false);
      return;
    }
    if (!editor || editor.isDestroyed) return;

    const t = window.setTimeout(() => {
      if (!editor.isDestroyed) {
        const view = editor.view;
        const docEnd = editor.state.doc.content.size;
        const startPos = 1;
        let firstLineTop: number | null = null;
        try {
          firstLineTop = view.coordsAtPos(startPos).top;
        } catch {
          // coordsAtPos can throw before the view is fully ready
        }
        if (firstLineTop !== null && docEnd >= startPos) {
          let lo = startPos;
          let hi = docEnd;
          while (lo < hi) {
            const mid = Math.ceil((lo + hi) / 2);
            let topAtMid: number | null = null;
            try {
              topAtMid = view.coordsAtPos(mid).top;
            } catch {
              hi = mid - 1;
              continue;
            }
            if (Math.abs(topAtMid - firstLineTop) <= 2) {
              lo = mid;
            } else {
              hi = mid - 1;
            }
          }
          editor.chain().focus().setTextSelection(lo).run();
        }
      }
      setPendingFirstLineFocus(false);
    }, 0);
    return () => clearTimeout(t);
  }, [pendingFirstLineFocus, isEditing, editor]);

  return (
    <>
      <div
        ref={containerRef}
        className={`group/desc relative flex-1 min-h-0 flex flex-col rounded-lg border border-dashed transition-[border-color,background-color,box-shadow] duration-200 ease-out ${
          isDragOver
            ? "border-blue-400/55 dark:border-blue-400/45 bg-blue-50/40 dark:bg-blue-950/20"
            : "border-transparent bg-transparent"
        }${isContentEmpty ? "min-h-[160px]" : ""}${
          !isEditing && !isDragOver
            ? "ticket-desc-hover-blink-border hover:bg-hover-effect dark:hover:bg-hover-effect-dk hover:shadow-sm"
            : ""
        }`}
        onClick={
          !isEditing && !isContentEmpty ? handleReadModeClick : undefined
        }
        {...dragProps}
      >
        {isDragOver && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-2">
              <ImageIcon className="h-8 w-8 text-blue-400 dark:text-blue-500" />
              <p className="text-sm font-medium text-blue-500 dark:text-blue-400">
                Drop images here
              </p>
            </div>
          </div>
        )}

        <DescriptionHeading
          onClick={!isEditing ? handleHeadingClick : undefined}
          showGuide={isHelpdeskSupport}
        />

        {showEditor ? (
          <div
            role="presentation"
            className={`relative ${
              isEditing || isExpanded
                ? innerColumnClass
                : `w-full ${innerInsetClass}`
            }${!isEditing ? "cursor-text" : ""}${isDragOver ? " pointer-events-none opacity-30" : ""}`}
            onClick={!isEditing ? handleReadModeClick : undefined}
          >
            <div
              ref={editorScrollRef}
              className={`flex min-w-0 flex-col ${!isEditing && "pt-3"} ${
                !isEditing && !isExpanded
                  ? "overflow-hidden"
                  : `flex-1 min-h-0 overflow-y-auto ${visibleScrollbar("[&::-webkit-scrollbar]:w-[6px]")}`
              }`}
              style={
                !isEditing && !isExpanded
                  ? {
                      maxHeight: `calc(100dvh - var(--ticket-header-height, 380px) - ${isBannerVisible ? 125 : 90}px)`,
                    }
                  : undefined
              }
            >
              {!isEditing && isContentEmpty && (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-0">
                  {isHelpdeskSupport ? PLACEHOLDER_NODE : DEFAULT_PLACEHOLDER_NODE}
                </div>
              )}
              <RichTextEditor
                imgMaxWidth="99%"
                message={content}
                editable={isEditing}
                onMessageChange={isEditing ? handleMessageChange : undefined}
                onEditorReady={handleEditorReady}
                placeholder=" "
                mentionActiveUsersList={mentionActiveUsersList}
                setTaggedUsers={setTaggedUsers}
              />
            </div>

            {!isEditing && isOverflowing && !isExpanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center justify-end pb-1.5 pt-10 bg-gradient-fade-bottom">
                <button
                  type="button"
                  onClick={handleExpand}
                  className="pointer-events-auto font-semibold flex items-center gap-1 min-h-[26px] px-6 rounded-md bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk shadow-sm hover:shadow transition-shadow"
                >
                  <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                    Expand
                  </span>
                  <ChevronDownIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk stroke-2" />
                </button>
              </div>
            )}
            {!isEditing && isExpanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center justify-end pb-1.5 pt-3">
                <button
                  type="button"
                  onClick={handleCollapse}
                  className="pointer-events-auto font-semibold flex items-center gap-1 min-h-[26px] px-6 rounded-md bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk shadow-sm hover:shadow transition-shadow"
                >
                  <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                    Collapse
                  </span>
                  <ChevronUpIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk stroke-2" />
                </button>
              </div>
            )}
          </div>
        ) : isContentEmpty ? (
          <div
            role="presentation"
            onClick={handleReadModeClick}
            className={`flex flex-1 min-h-0 cursor-text flex-col ${isDragOver ? "pointer-events-none opacity-30" : ""}`}
          >
            <div className={`flex flex-1 items-start ${innerInsetClass}`}>
              {isHelpdeskSupport ? PLACEHOLDER_NODE : DEFAULT_PLACEHOLDER_NODE}
            </div>
            <TicketDescriptionDisabledToolbar />
          </div>
        ) : null}

        {showEditor &&
          !isEditing &&
          !isContentEmpty &&
          !isOverflowing &&
          !isExpanded && (
            <div className="mt-auto shrink-0">
              <TicketDescriptionDisabledToolbar />
            </div>
          )}

        {isEditing && editor && (
          <div className="sticky bottom-0 z-10 shrink-0 border-t border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk">
            <div className="flex items-center px-3 py-[6px] overflow-x-auto">
              <RichTextEditorToolbar
                editor={editor}
                schema={TOOLBAR_SCHEMA}
                onImageInsert={onImageInsert}
                toolbarExtraClass="!shadow-none !rounded-none !max-w-fit helpdesk-desc-toolbar"
                wrapperClass="min-w-0"
                mentionActiveUsersList={mentionActiveUsersList}
                setTaggedUsers={setTaggedUsers}
                groupedToolbarOrder
              />
              <div className="mx-[6px] h-[16px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
              <button
                type="button"
                onClick={handleImageUploadClick}
                className="shrink-0 flex items-center gap-[4px] rounded-[4px] px-[10px] py-1 text-verba-blue dark:text-verba-blue-dk bg-hover-effect dark:bg-hover-effect-dk"
              >
                <AttachFilesIcon className="w-[14px] h-[14px] shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">
                  Upload file
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageFileSelect}
        className="hidden"
      />

      {previewOpen && previewAttachmentsRef.current.length > 0 && (
        <AttachmentPreviewModal
          open={previewOpen}
          onOpenChange={handlePreviewOpenChange}
          attachments={previewAttachmentsRef.current}
          initialIndex={previewIndex}
          title="Description Image"
        />
      )}
    </>
  );
};

export default TicketDescriptionBox;
